import { Camera } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { normalizeImage } from '../utils/normalizeImage';

const DEFAULT_PHOTO_OPTIONS = {
  quality: 80,
  targetWidth: 600,
  targetHeight: 600,
  correctOrientation: true,
};

export type PhotoOptions = Partial<typeof DEFAULT_PHOTO_OPTIONS>;

export interface PhotoResult {
  base64: string;
  contentType: string;
}

// Detect MIME type from base64 magic bytes rather than relying on API-provided format fields
function detectMimeType(base64: string): string {
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('iVBOR')) return 'image/png';
  if (base64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg';
}

export const usePhotoGallery = (overrides?: PhotoOptions) => {
  const photoOptions = { ...DEFAULT_PHOTO_OPTIONS, ...overrides };

  // Native apps already resize and compress the photo with photoOptions. On the
  // web the picker returns the original file, so it is re-encoded here as a JPEG
  // within the same size (throws UnsupportedImageError if it cannot be decoded).
  const toPhotoResult = async (base64: string): Promise<PhotoResult> => {
    if (Capacitor.isNativePlatform()) return { base64, contentType: detectMimeType(base64) };

    const normalized = await normalizeImage(base64, {
      maxWidth: photoOptions.targetWidth,
      maxHeight: photoOptions.targetHeight,
      quality: photoOptions.quality,
    });
    return { base64: normalized, contentType: 'image/jpeg' };
  };

  const takePhoto = async (): Promise<PhotoResult> => {
    const result = await Camera.takePhoto(photoOptions);
    if (!result.thumbnail) throw new Error('No thumbnail returned by camera');
    return toPhotoResult(result.thumbnail);
  };

  const chooseFromGallery = async (): Promise<PhotoResult> => {
    const result = await Camera.chooseFromGallery({ ...photoOptions, limit: 1 });
    const photo = result.results[0];
    if (!photo?.thumbnail) throw new Error('No thumbnail returned by gallery');
    return toPhotoResult(photo.thumbnail);
  };

  return { takePhoto, chooseFromGallery };
};
